import React from "react";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";
import type { TransformedProduct } from "../../../types/productApiTypes";

interface PriceData {
  formattedPrice: string;
  formattedRegularPrice: string;
  formattedSalePrice: string | null;
  onSale: boolean;
}

interface ProductInformationProps {
  productName: string;
  priceData: PriceData;
  stockStatus: TransformedProduct["stockStatus"];
  shortDescription: string | null;
  variationsLoading: boolean;
  shouldDisplayPrice: boolean;
  isVariable: boolean;
  currencySymbol: string;
  isCombinationAvailable: boolean;
}

const ProductInformation: React.FC<ProductInformationProps> = ({
  productName,
  priceData,
  stockStatus,
  shortDescription,
  variationsLoading,
  shouldDisplayPrice,
  isVariable,
  currencySymbol,
  isCombinationAvailable, // Destructure the new prop
}) => {
  const { t } = useTranslation(); // Keep for 't' function

  // Use the loading state passed from the parent directly
  const isLoading = variationsLoading;

  return (
    <>
      <h1 className="product-title">{productName}</h1>

      <div className="product-price">
        {isLoading ? ( // Use combined loading state
          <Skeleton height={28} width={150} className="price-skeleton" />
        ) : shouldDisplayPrice ? (
          <>
            {priceData.onSale && priceData.formattedSalePrice ? (
              <>
                <span className="regular-price">
                  {priceData.formattedRegularPrice}
                  {currencySymbol}
                </span>
                <span className="sale-price">
                  {priceData.formattedSalePrice}
                  {currencySymbol}
                </span>
              </>
            ) : (
              <span className="regular-price current-price">
                {priceData.formattedPrice}
                {currencySymbol}
              </span>
            )}
          </>
        ) : (
          isVariable && <span className="price-placeholder"> </span>
        )}
      </div>

      {/* Conditionally render stock status */}
      {!(isVariable && !isCombinationAvailable) && (
        <div className="stock-status">
          {isLoading ? ( // Use combined loading state
            <span className="loading-status">
              {t("productModal.checkingAvailability")} {/* Translate */}
            </span>
          ) : stockStatus === "outofstock" ? (
            <span className="out-of-stock">
              {t("product.outOfStock")} {/* Translate */}
            </span>
          ) : stockStatus === "onbackorder" ? (
            <span className="on-backorder">
              {t("product.onBackorder")} {/* Translate */}
            </span>
          ) : (
            <span className="in-stock">{t("product.inStock")}</span> // Translate
          )}
        </div>
      )}

      {shortDescription && (
        <div
          className="product-short-description"
          dangerouslySetInnerHTML={{ __html: shortDescription }}
        />
      )}
    </>
  );
};

export default ProductInformation;
